import React, { useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  Avatar,
  Button,
  TextField,
  Grid,
  Divider,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
} from '@mui/material';
import {
  Edit as EditIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  Person as PersonIcon,
  Phone as PhoneIcon,
  Email as EmailIcon,
  LocationOn as LocationIcon,
  Business as BusinessIcon,
  CalendarToday as CalendarIcon,
  Verified as VerifiedIcon,
  ShoppingCart as ShoppingCartIcon,
  Star as StarIcon,
} from '@mui/icons-material';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';

const Profile: React.FC = () => {
  const { user, updateProfile } = useAuth();
  const { t } = useLanguage();
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [alert, setAlert] = useState<{ type: 'success' | 'error'; message: string } | null>(null);
  
  // Form state
  const [formData, setFormData] = useState({
    firstName: user?.firstName || '',
    lastName: user?.lastName || '',
    email: user?.email || '',
    phone: user?.phone || '',
    company: user?.company || '',
    address: user?.address || '',
  });

  const handleEdit = () => {
    setFormData({
      firstName: user?.firstName || '',
      lastName: user?.lastName || '',
      email: user?.email || '',
      phone: user?.phone || '',
      company: user?.company || '',
      address: user?.address || '',
    });
    setShowEditDialog(true);
  };

  const handleSave = async () => {
    try {
      await updateProfile(formData);
      setAlert({ type: 'success', message: t.profile.profileUpdated });
      setShowEditDialog(false);
    } catch (error) {
      setAlert({ type: 'error', message: t.profile.profileUpdateError });
    }
  };

  const handleCancel = () => {
    setShowEditDialog(false);
  };

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const stats = [
    { label: t.profile.stats.orders, value: '12', icon: <ShoppingCartIcon />, color: '#3b82f6' },
    { label: t.profile.stats.favorites, value: '8', icon: <StarIcon />, color: '#f59e0b' },
    { label: t.profile.stats.reviews, value: '5', icon: <StarIcon />, color: '#10b981' },
  ];

  const recentOrders = [
    { id: '1', date: '2024-01-15', status: 'Livré', total: '125,000 FCFA' },
    { id: '2', date: '2024-01-10', status: 'En cours', total: '89,500 FCFA' },
    { id: '3', date: '2024-01-05', status: 'Livré', total: '156,000 FCFA' },
  ];

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      {alert && (
        <Alert 
          severity={alert.type} 
          sx={{ mb: 2 }}
          onClose={() => setAlert(null)}
        >
          {alert.message}
        </Alert>
      )}

      <Typography variant="h4" component="h1" gutterBottom sx={{ fontWeight: 'bold' }}>
        Mon Profil
      </Typography>

      <Grid container spacing={3}>
        {/* Profile Header */}
        <Grid item xs={12}>
          <Paper sx={{ p: 3, borderRadius: 2 }}>
            <Box display="flex" alignItems="center" justifyContent="space-between" mb={3}>
              <Box display="flex" alignItems="center" gap={2}>
                <Avatar
                  sx={{ 
                    width: 80, 
                    height: 80, 
                    bgcolor: '#1e40af',
                    fontSize: '2rem',
                    fontWeight: 'bold'
                  }}
                >
                  {user?.firstName?.charAt(0)}{user?.lastName?.charAt(0)}
                </Avatar>
                <Box>
                  <Typography variant="h5" component="h2" sx={{ fontWeight: 'bold' }}>
                    {user?.firstName} {user?.lastName}
                  </Typography>
                  <Box display="flex" alignItems="center" gap={1}>
                    <Chip 
                      icon={<VerifiedIcon />} 
                      label={t.profile.verifiedAccount} 
                      color="success" 
                      size="small" 
                    />
                    <Typography variant="body2" color="text.secondary">
                      {t.profile.memberSince} {new Date(user?.createdAt || Date.now()).toLocaleDateString('fr-FR')}
                    </Typography>
                  </Box>
                </Box>
              </Box>
              <Button
                variant="outlined"
                startIcon={<EditIcon />}
                onClick={handleEdit}
                sx={{ borderRadius: 2 }}
              >
                {t.profile.edit}
              </Button>
            </Box>

            {/* Stats */}
            <Grid container spacing={2} mb={3}>
              {stats.map((stat, index) => (
                <Grid item xs={12} sm={4} key={index}>
                  <Card sx={{ textAlign: 'center', borderRadius: 2 }}>
                    <CardContent>
                      <Box sx={{ color: stat.color, mb: 1 }}>
                        {stat.icon}
                      </Box>
                      <Typography variant="h4" component="div" sx={{ fontWeight: 'bold', color: stat.color }}>
                        {stat.value}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {stat.label}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </Paper>
        </Grid>

        {/* Profile Details */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3, borderRadius: 2, height: 'fit-content' }}>
            <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold', mb: 2 }}>
              {t.profile.personalInfo}
            </Typography>
            
            <List>
              <ListItem>
                <ListItemIcon>
                  <PersonIcon color="primary" />
                </ListItemIcon>
                <ListItemText
                  primary={t.profile.fullName}
                  secondary={`${user?.firstName} ${user?.lastName}`}
                />
              </ListItem>
              
              <ListItem>
                <ListItemIcon>
                  <PhoneIcon color="primary" />
                </ListItemIcon>
                <ListItemText
                  primary={t.profile.phone}
                  secondary={user?.phone || t.profile.notProvided}
                />
              </ListItem>
              
              <ListItem>
                <ListItemIcon>
                  <EmailIcon color="primary" />
                </ListItemIcon>
                <ListItemText
                  primary={t.profile.email}
                  secondary={user?.email || t.profile.notProvided}
                />
              </ListItem>
              
              <ListItem>
                <ListItemIcon>
                  <BusinessIcon color="primary" />
                </ListItemIcon>
                <ListItemText
                  primary={t.profile.company}
                  secondary={user?.company || t.profile.notProvided}
                />
              </ListItem>
              
              <ListItem>
                <ListItemIcon>
                  <LocationIcon color="primary" />
                </ListItemIcon>
                <ListItemText
                  primary={t.profile.address}
                  secondary={user?.address || t.profile.notProvided}
                />
              </ListItem>
              
              <ListItem>
                <ListItemIcon>
                  <CalendarIcon color="primary" />
                </ListItemIcon>
                <ListItemText
                  primary={t.profile.registrationDate}
                  secondary={new Date(user?.createdAt || Date.now()).toLocaleDateString('fr-FR')}
                />
              </ListItem>
            </List>
          </Paper>
        </Grid>

        {/* Recent Orders */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3, borderRadius: 2, height: 'fit-content' }}>
            <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold', mb: 2 }}>
              {t.profile.recentOrders}
            </Typography>
            
            <List>
              {recentOrders.map((order, index) => (
                <React.Fragment key={order.id}>
                  <ListItem>
                    <ListItemIcon>
                      <ShoppingCartIcon color="primary" />
                    </ListItemIcon>
                    <ListItemText
                      primary={`Commande #${order.id}`}
                      secondary={`${order.date} - ${order.total}`}
                    />
                    <Chip 
                      label={order.status} 
                      color={order.status === 'Livré' ? 'success' : 'warning'}
                      size="small"
                    />
                  </ListItem>
                  {index < recentOrders.length - 1 && <Divider />}
                </React.Fragment>
              ))}
            </List>
            
            <Box mt={2}>
              <Button variant="outlined" fullWidth>
                {t.profile.viewAllOrders}
              </Button>
            </Box>
          </Paper>
        </Grid>
      </Grid>

      {/* Edit Profile Dialog */}
      <Dialog open={showEditDialog} onClose={handleCancel} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
            {t.profile.editProfile}
          </Typography>
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label={t.profile.firstName}
                value={formData.firstName}
                onChange={(e) => handleInputChange('firstName', e.target.value)}
                margin="normal"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label={t.profile.lastName}
                value={formData.lastName}
                onChange={(e) => handleInputChange('lastName', e.target.value)}
                margin="normal"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label={t.profile.email}
                type="email"
                value={formData.email}
                onChange={(e) => handleInputChange('email', e.target.value)}
                margin="normal"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label={t.profile.phone}
                value={formData.phone}
                onChange={(e) => handleInputChange('phone', e.target.value)}
                margin="normal"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label={t.profile.company}
                value={formData.company}
                onChange={(e) => handleInputChange('company', e.target.value)}
                margin="normal"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label={t.profile.address}
                multiline
                rows={3}
                value={formData.address}
                onChange={(e) => handleInputChange('address', e.target.value)}
                margin="normal"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button onClick={handleCancel} startIcon={<CancelIcon />}>
            {t.profile.cancel}
          </Button>
          <Button 
            onClick={handleSave} 
            variant="contained" 
            startIcon={<SaveIcon />}
            sx={{ 
              background: 'linear-gradient(135deg, #1e40af 0%, #3b82f6 100%)',
              '&:hover': {
                background: 'linear-gradient(135deg, #1e3a8a 0%, #2563eb 100%)',
              }
            }}
          >
            {t.profile.save}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default Profile; 